/**
 * Keyword-based product matcher for the Shopper Concierge.
 *
 * We don't run a second LLM call to pick suggestions (adds latency + cost).
 * Instead we score each design on a small vocabulary of category / sub-category
 * / gender / occasion keywords and return the top N. Good enough for
 * "gold rings under 8g" / "gift for mum" / "temple necklace for wedding".
 */

const CATEGORY_KEYWORDS = {
  gold: ["gold"],
  silver: ["silver"],
  "rose-gold": ["rose gold", "rose-gold", "rosegold"],
  diamond: ["diamond", "solitaire"],
  gemstones: ["gemstone", "ruby", "emerald", "sapphire", "coral", "pearl"],
  "temple-antique": ["temple", "antique", "nakshi", "kundan", "polki", "heritage"],
};

const SUBCAT_KEYWORDS = {
  rings: ["ring"],
  bangles: ["bangle", "kada"],
  chains: ["chain", "harram"],
  jhumkas: ["jhumka", "jhumki"],
  earrings: ["earring", "stud"],
  tops: ["top"],
  necklaces: ["necklace", "haar"],
  mangalsutra: ["mangalsutra", "mangal sutra"],
  pendants: ["pendant", "locket"],
  murti: ["murti", "idol"],
};

const GENDER_KEYWORDS = {
  Men: ["men", "male", "him", "father", "dad", "husband", "brother"],
  Ladies: [
    "ladies",
    "women",
    "woman",
    "her",
    "mum",
    "mom",
    "mother",
    "wife",
    "sister",
    "daughter",
    "girl",
    "bride",
    "bridal",
  ],
  Kids: ["kids", "kid", "child", "children", "baby", "infant"],
};

const OCCASION_KEYWORDS = {
  bridal: ["bridal", "wedding", "marriage", "shaadi", "engagement"],
  daily: ["daily", "office", "work", "casual", "everyday", "delicate"],
  gift: ["gift", "gifting", "present", "anniversary", "birthday"],
  festive: ["festive", "diwali", "rakhi", "raksha bandhan", "karva chauth", "eid"],
};

const norm = (s) => (s || "").toLowerCase();

/**
 * Match the top `limit` designs against a free-text query.
 * Returns an array of designs sorted by score (highest first). Empty when
 * nothing scores > 0 — the concierge simply skips showing suggestions then.
 */
export function matchDesigns(query, designs, limit = 3) {
  const q = norm(query);
  if (!q || !designs.length) return [];
  const scored = [];

  for (const d of designs) {
    let score = 0;
    const catSlug = d.category?.slug;
    const subSlug = d.subCategory?.slug;

    // Sub-category keywords are the strongest signal ("ring", "chain", …).
    const subWords = SUBCAT_KEYWORDS[subSlug] || [];
    for (const w of subWords) if (q.includes(w)) score += 5;

    // Category keywords second.
    const catWords = CATEGORY_KEYWORDS[catSlug] || [];
    for (const w of catWords) if (q.includes(w)) score += 3;

    // Gender.
    const genderWords = GENDER_KEYWORDS[d.gender] || [];
    for (const w of genderWords) if (q.includes(w)) score += 2;

    // Bridal / gift bumps for temple-antique or heavy weights.
    for (const [key, words] of Object.entries(OCCASION_KEYWORDS)) {
      if (!words.some((w) => q.includes(w))) continue;
      if (key === "bridal" && (catSlug === "temple-antique" || catSlug === "diamond")) score += 2;
      if (key === "daily" && (subSlug === "rings" || subSlug === "chains" || subSlug === "earrings")) score += 1;
      if (key === "gift" && (subSlug === "pendants" || subSlug === "earrings" || subSlug === "chains")) score += 1;
      if (key === "festive" && (catSlug === "gold" || catSlug === "temple-antique")) score += 1;
    }

    // Free-text hits on design name / style name.
    const hay = norm(`${d.name} ${d.styleName || ""}`);
    for (const word of q.split(/\s+/)) {
      if (word.length > 3 && hay.includes(word)) score += 1;
    }

    // Weight window ("under 8g" / "below 12 gram").
    const weightMatch = q.match(/(?:under|below|less than|<=?)\s*(\d+(?:\.\d+)?)\s*g/);
    if (weightMatch) {
      const cap = parseFloat(weightMatch[1]);
      if (d.weight && d.weight <= cap) score += 2;
      else if (d.weight && d.weight > cap) score -= 3;
    }

    if (score > 0) scored.push({ d, score });
  }

  scored.sort((a, b) => b.score - a.score);
  // Dedupe by id defensively — merged catalogs can contain repeats.
  const seen = new Set();
  const out = [];
  for (const { d } of scored) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    out.push(d);
    if (out.length >= limit) break;
  }
  return out;
}
